import React, { useState, useEffect, JSX } from 'react';
import { BackstoryElementProps } from './BackstoryTab';
import * as Types from 'types/types';
import { Box, Button, TextField } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import { Scrollable } from './Scrollable';

interface DocumentViewProps extends BackstoryElementProps {
  document: Types.Document;
  edit?: boolean;
  onSave?: (document: Types.Document) => void;
  onCancel?: () => void;
}

const DocumentView = (props: DocumentViewProps): JSX.Element => {
  const { apiClient } = useAuth();
  const { setSnack } = useAppState();
  const { document, edit = false, onSave, onCancel } = props;
  const [editingName, setEditingName] = useState<string>(document.filename);
  const [content, setContent] = useState<string>('');
  const [editContent, setEditContent] = useState<string>('');

  useEffect(() => {
    if (!document) {
      return;
    }
    const fetchDocument = async (): Promise<void> => {
      try {
        const response: Types.DocumentContentResponse = await apiClient.getCandidateDocumentText(
          document
        );
        setContent(response.content || '');
        setEditContent(response.content || '');
      } catch (error) {
        console.error('Error obtaining Docs content information:', error);
        setContent(`${document.filename} not found.`);
      }
    };

    fetchDocument();
  }, [document, setContent]);

  // Handle document rename
  const handleDocumentUpdate = async (): Promise<void> => {
    if (!editingName.trim()) {
      setSnack('Document name cannot be empty', 'error');
      return;
    }
    if (!editContent.trim()) {
      setSnack('Document content cannot be empty. Delete instead.', 'error');
      return;
    }

    try {
      // Call API to rename document
      document.filename = editingName;
      let result: Types.Document;
      if (editContent !== content) {
        result = await apiClient.updateCandidateDocument(document, editContent);
      } else {
        result = await apiClient.updateCandidateDocument(document);
      }
      onSave && onSave(result);
      setContent(editContent);
      setSnack('Document updated successfully', 'success');
    } catch (error) {
      setSnack('Failed to udpate document', 'error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        position: 'relative',
        overflow: 'auto',
        height: '100%',
      }}
    >
      <TextField
        autoFocus
        margin="dense"
        label="Document Name"
        fullWidth
        variant="outlined"
        value={editingName}
        onChange={(e): void => {
          edit && setEditingName(e.target.value);
        }}
        onKeyUp={(e): void => {
          if (e.key === 'Enter') {
            handleDocumentUpdate();
          }
        }}
        sx={{ pointerEvents: edit ? 'auto' : 'none' }}
      />
      <Scrollable sx={{ width: '100%', height: 'calc(100% - 64px)', overflowY: 'auto' }}>
        <TextField
          margin="dense"
          label="Content"
          fullWidth
          variant="outlined"
          value={editContent}
          onChange={(e): void => {
            edit && setEditContent(e.target.value);
          }}
          sx={{ pointerEvents: edit ? 'auto' : 'none' }}
          multiline
        />
      </Scrollable>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, direction: 'row' }}>
        {edit && (
          <Button
            onClick={(): void => {
              handleDocumentUpdate();
            }}
            variant="contained"
            disabled={
              !editingName.trim() ||
              !editContent.trim() ||
              (editingName === document.filename && editContent === content)
            }
          >
            Save
          </Button>
        )}
        <Button
          onClick={(): void => {
            onCancel && onCancel();
          }}
        >
          {edit && (editingName !== document.filename || editContent !== content)
            ? 'Cancel'
            : 'Close'}
        </Button>
      </Box>
    </Box>
  );
};

export { DocumentView };
